/**
 * [Phase 2c-D §6.7 NEW-10] One-time, dismissible hint badge that points users
 * at the speaker icon on Adam's activity rows. Light, friendly, persistent
 * via localStorage so it never resurfaces after dismissal.
 *
 * Usage: wrap the FIRST speaker icon on a page in <ListenWithAdamHint>.
 * The hint mounts once per user (across sessions) and self-dismisses on
 * either explicit "got it" click or the first time the wrapped speaker
 * icon is clicked (whichever comes first).
 */
import { useState, useEffect } from "react";
import { X } from "lucide-react";

const STORAGE_KEY = "pgf_listen_with_adam_hint_dismissed_v1";

export default function ListenWithAdamHint({ children }) {
  const [dismissed, setDismissed] = useState(true); // start hidden, hydrate on mount
  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(STORAGE_KEY) === "1");
    } catch (_e) {
      setDismissed(true);
    }
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_e) { /* noop */ }
    setDismissed(true);
  };

  if (dismissed) return children;

  return (
    <span className="relative inline-block" data-testid="listen-with-adam-hint">
      {/* Wrap the target icon; clicking it also dismisses the hint. */}
      <span onClickCapture={dismiss}>{children}</span>
      <span
        className="absolute -top-12 right-0 z-30 w-[220px] rounded-xl bg-[#FF6B00] text-white shadow-[0_8px_24px_rgba(255,107,0,0.35)] px-3 py-2 text-[11px] font-display font-bold pointer-events-auto"
        style={{ animation: "pgf-pulse 1.6s ease-in-out infinite" }}
      >
        <div className="flex items-start gap-2">
          <span className="flex-1 leading-tight">
            🎙️ Tap to hear Adam read it aloud
          </span>
          <button
            type="button"
            onClick={dismiss}
            data-testid="listen-with-adam-hint-dismiss"
            aria-label="Dismiss"
            className="w-5 h-5 rounded flex items-center justify-center hover:bg-white/20 transition-colors shrink-0"
          >
            <X size={11} />
          </button>
        </div>
        {/* Arrow pointer */}
        <span
          className="absolute bottom-0 right-3 translate-y-full w-0 h-0"
          style={{
            borderLeft: "6px solid transparent",
            borderRight: "6px solid transparent",
            borderTop: "6px solid #FF6B00",
          }}
        />
      </span>
    </span>
  );
}
